// Valet Booking Frontend - Matching MenuPage.jsx UI/Layout Style
import React, { useState, useEffect } from 'react';
import {
  Car, Calendar, User, Users, Plus, Edit2, Trash2, Eye, Search,
  ArrowLeft, CheckCircle, AlertCircle, TrendingUp, Link, Zap, Loader,
  X, Phone, Mail, MapPin
} from 'lucide-react';
import CalendarPage from './CalendarPage';
import SettingsPage from './SettingsPage';
import ValetManagement from './ValetManagement';
import ValetListView from './ValetListView';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Status Badge Component
const StatusBadge = ({ status }) => {
  const statusConfig = {
    pending: { bg: 'bg-amber-100', text: 'text-amber-800', icon: '⏳', label: 'Pending' },
    assigned: { bg: 'bg-blue-100', text: 'text-blue-800', icon: '✓', label: 'Assigned' },
    in_progress: { bg: 'bg-blue-100', text: 'text-blue-800', icon: '➡️', label: 'In Progress' },
    completed: { bg: 'bg-green-100', text: 'text-green-800', icon: '✅', label: 'Complete' },
    cancelled: { bg: 'bg-red-100', text: 'text-red-800', icon: '❌', label: 'Cancelled' },
    amended: { bg: 'bg-purple-100', text: 'text-purple-800', icon: '📝', label: 'Amended' }
  };

  const config = statusConfig[status] || statusConfig.pending;

  return (
    <span className={`px-3 py-1 rounded-full text-sm font-medium ${config.bg} ${config.text}`}>
      {config.icon} {config.label}
    </span>
  );
};

// Priority Badge Component
const PriorityBadge = ({ priority }) => {
  const priorityConfig = {
    normal: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Normal' },
    high: { bg: 'bg-orange-100', text: 'text-orange-700', label: 'High' },
    urgent: { bg: 'bg-red-100', text: 'text-red-700', label: 'Urgent' }
  };

  const config = priorityConfig[priority] || priorityConfig.normal;

  return (
    <span className={`px-2 py-1 rounded text-xs font-medium ${config.bg} ${config.text}`}>
      {config.label}
    </span>
  );
};

// Source Badge Component
const SourceBadge = ({ source }) => {
  if (source === 'preparation_tracker') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-purple-100 text-purple-700">
        <Zap className="w-3 h-3" /> Prep Tracker
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-700">
      <User className="w-3 h-3" /> Manual
    </span>
  );
};

// Loading Spinner
const LoadingSpinner = () => (
  <div className="flex justify-center items-center py-12">
    <Loader className="w-8 h-8 animate-spin text-blue-600" />
  </div>
);

// Notification Component
const Notification = ({ message, type = 'success', onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 4000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const bgColor = type === 'success' ? 'bg-green-100 border-green-300' : 'bg-red-100 border-red-300';
  const textColor = type === 'success' ? 'text-green-800' : 'text-red-800';
  const icon = type === 'success' ? '✓' : '⚠';

  return (
    <div className={`fixed top-4 right-4 ${bgColor} ${textColor} px-6 py-4 rounded-lg border shadow-lg z-50`}>
      <div className="flex items-center gap-3">
        <span className="text-xl">{icon}</span>
        <span>{message}</span>
      </div>
    </div>
  );
};

// Create Booking Modal
const CreateBookingModal = ({ isOpen, onClose, onSuccess, valets }) => {
  const [formData, setFormData] = useState({
    vehicle_make: '',
    vehicle_model: '',
    vehicle_registration: '',
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    booking_date: '',
    booking_time: '09:00',
    return_date: '',
    return_time: '17:00',
    valet_id: '',
    priority: 'normal',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Get valet name from selected valet_id
      const selectedValet = valets.find(v => v.valet_id === formData.valet_id);
      const valetName = selectedValet?.name || null;

      const bookingData = {
        ...formData,
        valet_name: valetName
      };

      const response = await fetch(`${API_URL}/api/valet-bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bookingData)
      });

      if (!response.ok) {
        throw new Error('Failed to create booking');
      }

      onSuccess('Booking created successfully!');
      onClose();
      setFormData({
        vehicle_make: '',
        vehicle_model: '',
        vehicle_registration: '',
        customer_name: '',
        customer_email: '',
        customer_phone: '',
        booking_date: '',
        booking_time: '09:00',
        return_date: '',
        return_time: '17:00',
        valet_id: '',
        priority: 'normal',
        notes: ''
      });
    } catch (err) {
      setError(err.message || 'Error creating booking');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-4 flex justify-between items-center border-b">
          <div className="flex items-center gap-3">
            <Car className="w-6 h-6" />
            <h2 className="text-2xl font-bold">Create New Booking</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-blue-500 rounded-lg transition"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Modal Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          {/* Vehicle Section */}
          <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
            <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Car className="w-5 h-5" /> Vehicle Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Vehicle Make *</label>
                <input
                  type="text"
                  name="vehicle_make"
                  value={formData.vehicle_make}
                  onChange={handleChange}
                  placeholder="e.g., Toyota"
                  required
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Vehicle Model *</label>
                <input
                  type="text"
                  name="vehicle_model"
                  value={formData.vehicle_model}
                  onChange={handleChange}
                  placeholder="e.g., Camry"
                  required
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Registration</label>
                <input
                  type="text"
                  name="vehicle_registration"
                  value={formData.vehicle_registration}
                  onChange={handleChange}
                  placeholder="e.g., ABC-123"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Customer Section */}
          <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
            <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <User className="w-5 h-5" /> Customer Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Customer Name *</label>
                <input
                  type="text"
                  name="customer_name"
                  value={formData.customer_name}
                  onChange={handleChange}
                  placeholder="e.g., John Smith"
                  required
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Email</label>
                <input
                  type="email"
                  name="customer_email"
                  value={formData.customer_email}
                  onChange={handleChange}
                  placeholder="john@example.com"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Phone</label>
                <input
                  type="tel"
                  name="customer_phone"
                  value={formData.customer_phone}
                  onChange={handleChange}
                  placeholder="555-1234"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Dates Section */}
          <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
            <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5" /> Booking Dates
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Start Date *</label>
                <input
                  type="date"
                  name="booking_date"
                  value={formData.booking_date}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Start Time</label>
                <input
                  type="time"
                  name="booking_time"
                  value={formData.booking_time}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Return Date *</label>
                <input
                  type="date"
                  name="return_date"
                  value={formData.return_date}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Return Time</label>
                <input
                  type="time"
                  name="return_time"
                  value={formData.return_time}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Valet & Priority Section */}
          <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
            <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Users className="w-5 h-5" /> Assignment
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Valet *</label>
                <select
                  name="valet_id"
                  value={formData.valet_id}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select a valet...</option>
                  {valets.map(valet => (
                    <option key={valet.valet_id} value={valet.valet_id}>
                      {valet.name} ({valet.status})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Priority</label>
                <select
                  name="priority"
                  value={formData.priority}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="normal">Normal</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
            </div>
          </div>

          {/* Notes Section */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Additional Notes</label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              placeholder="Any special requirements or notes..."
              rows="3"
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Form Actions */}
          <div className="flex gap-4 justify-end pt-6 border-t border-slate-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition font-medium disabled:opacity-50 shadow-lg shadow-blue-500/30"
            >
              {loading ? 'Creating...' : 'Create Booking'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Booking Card Component
const BookingCard = ({ booking, onView, onDelete, onReload }) => {
  const [deleting, setDeleting] = useState(false);
  const [completing, setCompleting] = useState(false);

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to cancel this booking?')) {
      setDeleting(true);
      try {
        const response = await fetch(`${API_URL}/api/valet-bookings/${booking.booking_id}`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ deleted_by: 'user' })
        });
        
        if (response.ok) {
          onReload();
        }
      } catch (err) {
        console.error('Delete error:', err);
      } finally {
        setDeleting(false);
      }
    }
  };

  const handleMarkCompleted = async () => {
    setCompleting(true);
    try {
      const response = await fetch(`${API_URL}/api/valet-bookings/${booking.booking_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'completed' })
      });
      
      if (response.ok) {
        onReload();
      }
    } catch (err) {
      console.error('Complete error:', err);
    } finally {
      setCompleting(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 hover:shadow-md transition">
      <div className="flex justify-between items-start mb-4">
        <div>
          <h3 className="text-lg font-bold text-slate-900">{booking.booking_code}</h3>
          <p className="text-sm text-slate-500">{booking.vehicle_make} {booking.vehicle_model}</p>
        </div>
        <div className="flex gap-2">
          <StatusBadge status={booking.status} />
          <SourceBadge source={booking.source} />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 text-sm">
        <div className="flex items-center gap-2 text-slate-600">
          <User className="w-4 h-4" />
          <span>{booking.customer_name}</span>
        </div>
        <div className="flex items-center gap-2 text-slate-600">
          <Users className="w-4 h-4" />
          <span>{booking.valet_name}</span>
        </div>
        <div className="flex items-center gap-2 text-slate-600">
          <Calendar className="w-4 h-4" />
          <span>{new Date(booking.booking_date).toLocaleDateString()}</span>
        </div>
      </div>

      <div className="flex gap-3">
        <button
          onClick={() => onView(booking)}
          className="flex-1 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition flex items-center justify-center gap-2 font-medium"
        >
          <Eye className="w-4 h-4" />
          View Details
        </button>
        <button
          onClick={handleMarkCompleted}
          disabled={completing}
          className="px-4 py-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition disabled:opacity-50 font-medium"
          title="Mark as Completed"
        >
          ✓
        </button>
        <button
          onClick={handleDelete}
          disabled={deleting}
          className="px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition disabled:opacity-50"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

// Main Component
export default function ValetBookingUI() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [bookings, setBookings] = useState([]);
  const [valets, setValets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSource, setFilterSource] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [notification, setNotification] = useState(null);

  // Fetch bookings
  const fetchBookings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/valet-bookings`);
      if (response.ok) {
        const data = await response.json();
        setBookings(data.data || []);
      }
    } catch (err) {
      console.error('Fetch bookings error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch valets
  const fetchValets = async () => {
    try {
      const response = await fetch(`${API_URL}/api/valets`);
      if (response.ok) {
        const data = await response.json();
        setValets(data.data || []);
      }
    } catch (err) {
      console.error('Fetch valets error:', err);
    }
  };

  useEffect(() => {
    fetchBookings();
    fetchValets();
  }, []);

  // Filter bookings
  const filteredBookings = bookings.filter(booking => {
    let matches = true;

    // Exclude completed bookings from 'all' and 'pending' tabs
    if (filterStatus === 'all' || filterStatus === 'pending') {
      if (booking.status === 'completed') {
        matches = false;
      }
    }
    
    if (filterStatus !== 'all' && booking.status !== filterStatus) {
      matches = false;
    }

    if (filterSource !== 'all' && booking.source !== filterSource) {
      matches = false;
    }

    if (searchTerm && !booking.booking_code.includes(searchTerm) && 
        !booking.customer_name.toLowerCase().includes(searchTerm.toLowerCase())) {
      matches = false;
    }

    return matches;
  });

  // Dashboard stats
  const stats = {
    total: bookings.length,
    inProgress: bookings.filter(b => b.status === 'pending' || b.status === 'assigned').length,
    completed: bookings.filter(b => b.status === 'completed').length,
    fromPrepTracker: bookings.filter(b => b.source === 'preparation_tracker').length
  };

  const handleViewBooking = (booking) => {
    setSelectedBooking(booking);
  };

  const handleCreateSuccess = (message) => {
    setNotification({ message, type: 'success' });
    fetchBookings();
  };

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: '📊' },
    { id: 'calendar', label: 'Book Now', icon: '📅' },
    { id: 'bookings', label: 'All Bookings', icon: '📋' },
    { id: 'valet-list', label: 'Valet List', icon: '👥' },
    { id: 'pending', label: 'Pending', icon: '⏳' },
    { id: 'completed', label: 'Completed', icon: '✅' },
    { id: 'settings', label: 'Settings', icon: '⚙️' }
  ];

  const tabDescriptions = {
    dashboard: 'Overview and summary of all valet bookings',
    bookings: 'Manage and view all valet bookings',
    pending: 'Bookings awaiting assignment or in preparation',
    completed: 'Successfully completed valet bookings'
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
      {/* HEADER */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200/60 shadow-sm">
        <div className="max-w-6xl mx-auto px-8 py-8">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-blue-800 rounded-xl flex items-center justify-center">
                <Car className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-4xl font-extrabold bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent">
                  Valet Booking System
                </h1>
                <p className="text-sm font-medium text-slate-500 mt-1">Professional vehicle booking management</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* TAB NAVIGATION */}
      <div className="max-w-6xl mx-auto px-8 py-8">
        <div className="flex gap-3 mb-10 overflow-x-auto pb-2">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold text-sm transition-all whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg shadow-blue-500/30'
                  : 'bg-white text-slate-700 border border-slate-200 hover:border-slate-300 hover:bg-slate-50'
              }`}
            >
              <span>{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>

        {/* CONTENT AREA */}
        <div className="space-y-8">
          {/* Tool Information */}
          <div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">
              {tabs.find(t => t.id === activeTab)?.label}
            </h2>
            <p className="text-slate-600">
              {tabDescriptions[activeTab]}
            </p>
          </div>

          {/* Content Based on Tab */}
          {activeTab === 'dashboard' && (
            <div className="space-y-8">
              {/* Stats Grid */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
                  <p className="text-slate-600 text-sm font-medium">Total Bookings</p>
                  <p className="text-4xl font-bold text-slate-900 mt-3">{stats.total}</p>
                </div>
                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
                  <p className="text-slate-600 text-sm font-medium">In Progress</p>
                  <p className="text-4xl font-bold text-amber-600 mt-3">{stats.inProgress}</p>
                </div>
                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
                  <p className="text-slate-600 text-sm font-medium">Completed</p>
                  <p className="text-4xl font-bold text-green-600 mt-3">{stats.completed}</p>
                </div>
                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
                  <p className="text-slate-600 text-sm font-medium">From Prep Tracker</p>
                  <p className="text-4xl font-bold text-purple-600 mt-3">{stats.fromPrepTracker}</p>
                </div>
              </div>

              {/* Recent Bookings section removed - Ready for enhanced dashboard data */}
            </div>
          )}

          {activeTab === 'calendar' && (
            <CalendarPage onNavigateBack={() => setActiveTab('dashboard')} />
          )}

          {activeTab === 'bookings' && (
            <div className="space-y-6">
              {/* Filters */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
                <h3 className="font-bold text-slate-900 mb-4">Filter & Search:</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Status</label>
                    <select
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="all">All Statuses</option>
                      <option value="pending">Pending</option>
                      <option value="in_progress">In Progress</option>
                      <option value="completed">Completed</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Source</label>
                    <select
                      value={filterSource}
                      onChange={(e) => setFilterSource(e.target.value)}
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="all">All Sources</option>
                      <option value="manual">Manual</option>
                      <option value="preparation_tracker">Prep Tracker</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Search</label>
                    <input
                      type="text"
                      placeholder="Search booking code or customer..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              {/* Booking List */}
              {loading ? (
                <LoadingSpinner />
              ) : (
                <div className="space-y-4">
                  {filteredBookings.length === 0 ? (
                    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-12 text-center">
                      <p className="text-slate-500">No bookings found</p>
                    </div>
                  ) : (
                    filteredBookings.map(booking => (
                      <BookingCard
                        key={booking.booking_id}
                        booking={booking}
                        onView={handleViewBooking}
                        onDelete={() => {}}
                        onReload={fetchBookings}
                      />
                    ))
                  )}
                </div>
              )}
            </div>
          )}


          {activeTab === 'pending' && (
            <div>
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
                <h3 className="font-bold text-slate-900 mb-4">Pending Bookings:</h3>
                <ul className="space-y-2 text-slate-700">
                  <li>✓ Awaiting valet assignment</li>
                  <li>✓ In preparation phase</li>
                  <li>✓ Not yet started</li>
                  <li>✓ Requires immediate attention</li>
                  <li>✓ Manage and reschedule as needed</li>
                </ul>
              </div>

              {loading ? (
                <LoadingSpinner />
              ) : (
                <div className="space-y-4 mt-6">
                  {bookings.filter(b => b.status === 'pending' || b.status === 'assigned').length === 0 ? (
                    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-12 text-center">
                      <p className="text-slate-500">No pending bookings</p>
                    </div>
                  ) : (
                    bookings.filter(b => b.status === 'pending' || b.status === 'assigned').map(booking => (
                      <BookingCard
                        key={booking.booking_id}
                        booking={booking}
                        onView={handleViewBooking}
                        onDelete={() => {}}
                        onReload={fetchBookings}
                      />
                    ))
                  )}
                </div>
              )}
            </div>
          )}

          {activeTab === 'completed' && (
            <div>
              {loading ? (
                <LoadingSpinner />
              ) : (
                <div className="space-y-4">
                  {bookings.filter(b => b.status === 'completed').length === 0 ? (
                    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-12 text-center">
                      <p className="text-slate-500">No completed bookings yet</p>
                    </div>
                  ) : (
                    bookings.filter(b => b.status === 'completed').map(booking => (
                      <BookingCard
                        key={booking.booking_id}
                        booking={booking}
                        onView={handleViewBooking}
                        onDelete={() => {}}
                        onReload={fetchBookings}
                      />
                    ))
                  )}
                </div>
              )}
            </div>
          )}

          {activeTab === 'valet-list' && (
            <ValetListView />
          )}

          {activeTab === 'settings' && (
            <SettingsPage onNavigateBack={() => setActiveTab('dashboard')} />
          )}
        </div>
      </div>

      {/* Booking Details Modal */}
      {selectedBooking && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-4 flex justify-between items-center">
              <h2 className="text-2xl font-bold">Booking Details</h2>
              <button
                onClick={() => setSelectedBooking(null)}
                className="p-2 hover:bg-blue-500 rounded-lg transition"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-slate-600 text-sm">Booking Code</p>
                  <p className="text-lg font-bold text-slate-900">{selectedBooking.booking_code}</p>
                </div>
                <div>
                  <p className="text-slate-600 text-sm">Status</p>
                  <StatusBadge status={selectedBooking.status} />
                </div>
              </div>

              <div className="border-t pt-6">
                <h3 className="font-semibold text-slate-900 mb-4">Vehicle Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-slate-600 text-sm">Make & Model</p>
                    <p className="text-slate-900">{selectedBooking.vehicle_make} {selectedBooking.vehicle_model}</p>
                  </div>
                  <div>
                    <p className="text-slate-600 text-sm">Registration</p>
                    <p className="text-slate-900">{selectedBooking.vehicle_registration}</p>
                  </div>
                </div>
              </div>

              <div className="border-t pt-6">
                <h3 className="font-semibold text-slate-900 mb-4">Customer Information</h3>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <p className="text-slate-600 text-sm">Name</p>
                    <p className="text-slate-900">{selectedBooking.customer_name}</p>
                  </div>
                  <div className="flex gap-4">
                    <div>
                      <p className="text-slate-600 text-sm">Email</p>
                      <p className="text-slate-900">{selectedBooking.customer_email}</p>
                    </div>
                    <div>
                      <p className="text-slate-600 text-sm">Phone</p>
                      <p className="text-slate-900">{selectedBooking.customer_phone}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="border-t pt-6">
                <h3 className="font-semibold text-slate-900 mb-4">Booking Dates</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-slate-600 text-sm">Start Date</p>
                    <p className="text-slate-900">{new Date(selectedBooking.booking_date).toLocaleDateString()}</p>
                  </div>
                  <div>
                    <p className="text-slate-600 text-sm">Return Date</p>
                    <p className="text-slate-900">{new Date(selectedBooking.return_date).toLocaleDateString()}</p>
                  </div>
                </div>
              </div>

              <div className="border-t pt-6">
                <h3 className="font-semibold text-slate-900 mb-4">Assignment</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-slate-600 text-sm">Assigned Valet</p>
                    <p className="text-slate-900">{selectedBooking.valet_name}</p>
                  </div>
                  <div>
                    <p className="text-slate-600 text-sm">Priority</p>
                    <PriorityBadge priority={selectedBooking.priority} />
                  </div>
                </div>
              </div>

              {selectedBooking.notes && (
                <div className="border-t pt-6">
                  <h3 className="font-semibold text-slate-900 mb-4">📝 Notes</h3>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <p className="text-sm text-amber-900">{selectedBooking.notes}</p>
                  </div>
                </div>
              )}

              <div className="border-t pt-6 flex gap-4 justify-end">
                <button
                  onClick={async () => {
                    try {
                      const response = await fetch(`${API_URL}/api/valet-bookings/${selectedBooking.booking_id}`, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ status: 'completed' })
                      });
                      if (response.ok) {
                        setNotification({ message: 'Booking marked as completed!', type: 'success' });
                        setSelectedBooking(null);
                        fetchBookings();
                      } else {
                        setNotification({ message: 'Failed to update booking', type: 'error' });
                      }
                    } catch (error) {
                      setNotification({ message: 'Error updating booking: ' + error.message, type: 'error' });
                    }
                  }}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-medium"
                >
                  ✓ Mark as Completed
                </button>
                <button
                  onClick={() => setSelectedBooking(null)}
                  className="px-6 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition font-medium"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Notification */}
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}
    </div>
  );
}
